import { belgeDugmeleri } from "../../components/receipts.js";
import { subeBaglaminda, subeBul } from "../../domain/context.js";
// ============================================================
// Veli portalı — Ödemelerim (§2)
//
// Velinin kendi çocuğunun hesabını gördüğü ekran. Tutarlar aynı
// domain/invoices.js üzerinden hesaplanır — kurum tarafıyla veli
// tarafının farklı rakam göstermesi mümkün değil.
// ============================================================

import { PAY, card, emptyNote, sectionTitle } from "../../components/ui.js";
import { DONEM } from "../../config.js";
import { avatar } from "../../lib/avatar.js";
import { donemAdi, esc, fmtDate, money } from "../../lib/fmt.js";
import { cocuklarim } from "../../domain/auth.js";
import { feeFor } from "../../domain/fees.js";
import { faturaDurumu, faturaTutari, hesapOzeti, tahsilEdilen } from "../../domain/invoices.js";

export function viewVeliOdemelerim() {
  const kids = cocuklarim();
  if (!kids.length) return emptyNote("Kayıtlı çocuk bulunamadı.");

  // Hesaplar aktif şubeye göre çalışır; her çocuk kendi şubesinin
  // bağlamında hesaplanır (kardeşler farklı şubede olabilir).
  const kartlar = kids.map(s => subeBaglaminda(s.branchId, () => cocukHesabi(s))).join("");
  const toplamKalan = kids.reduce(
    (a, s) => a + subeBaglaminda(s.branchId, () => hesapOzeti(s).kalan), 0);

  return `
  ${card(`
    <div class="p-5 flex items-center gap-4">
      <div class="min-w-0 flex-1">
        <div class="text-xs font-semibold text-inksoft">Toplam Açık Bakiye</div>
        <div class="font-display font-extrabold text-3xl mt-1 ${toplamKalan > 0 ? "text-coral" : "text-teal"}">
          ${money(toplamKalan)}
        </div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">
          ${kids.length} çocuk · ${donemAdi(DONEM)} dönemi dahil
        </div>
      </div>
      ${toplamKalan > 0 ? `
        <button class="rounded-xl bg-teal text-white text-sm font-semibold px-4 py-2.5 shrink-0" disabled
          title="Online ödeme entegrasyonu henüz kurulmadı">
          Online öde
        </button>` : ""}
    </div>`, "mb-4")}

  <div class="grid gap-4 items-start">${kartlar}</div>`;
}

function cocukHesabi(s) {
  const t = subeBul(s.branchId);
  const c = t.classes.find(x => x.id === s.classId);
  const hesap = hesapOzeti(s);
  const guncel = t.payments.find(p => p.studentId === s.id && p.period === DONEM);
  const fee = feeFor(s, guncel);

  return card(`
    <div class="p-5">
      <div class="flex items-center gap-3">
        ${avatar(s, "w-10 h-10")}
        <div class="min-w-0 flex-1">
          <div class="font-display font-bold truncate">${esc(s.name)}</div>
          <div class="text-xs text-inksoft">${esc(c.name)} · ${esc(t.name)}</div>
        </div>
        <div class="text-right shrink-0">
          <div class="font-display font-bold ${hesap.kalan > 0 ? "text-coral" : "text-teal"}">${money(hesap.kalan)}</div>
          <div class="text-[10px] text-inksoft">bakiye</div>
        </div>
      </div>

      <!-- Ücret dökümü -->
      <div class="rounded-xl bg-paper p-3 mt-4">
        <div class="text-[11px] font-bold text-inksoft uppercase mb-1.5">
          ${donemAdi(DONEM)} ücreti nasıl hesaplandı
        </div>
        <div class="space-y-0.5 text-[12px]">
          <div class="flex justify-between gap-3">
            <span class="text-inksoft">${fee.half ? "Yarım gün" : "Tam gün"} · ${esc(c.name)}</span>
            <span class="shrink-0">${money(fee.base)}</span>
          </div>
          ${fee.service ? `<div class="flex justify-between gap-3"><span class="text-inksoft">Servis</span><span class="shrink-0">${money(fee.service)}</span></div>` : ""}
          ${fee.discount ? `<div class="flex justify-between gap-3 text-teal"><span>${esc(fee.discountLabel)} (%${fee.pct})</span><span class="shrink-0">− ${money(fee.discount)}</span></div>` : ""}
          ${fee.override !== null ? `<div class="flex justify-between gap-3 text-grape"><span>Özel tutar${guncel?.note ? ` (${esc(guncel.note)})` : ""}</span><span class="shrink-0">${money(fee.override)}</span></div>` : ""}
        </div>
        <div class="flex justify-between font-display font-bold text-sm mt-2 pt-2 border-t border-line">
          <span>Toplam</span><span>${money(fee.total)}</span>
        </div>
      </div>

      <!-- Fatura geçmişi -->
      <div class="mt-4">
        ${sectionTitle("Fatura & Makbuz Geçmişi", `<span class="text-[11px] text-inksoft">${hesap.faturalar.length} dönem</span>`)}
        <div class="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          ${hesap.faturalar.map(inv => {
            const tutar = faturaTutari(s, inv);
            const durum = faturaDurumu(s, inv);
            const odenen = tahsilEdilen(inv);
            return `
            <div class="rounded-lg border border-line px-3 py-2">
              <div class="flex items-center justify-between gap-2">
                <span class="text-xs font-semibold">${donemAdi(inv.period)}</span>
                <span class="flex items-center gap-2 shrink-0">
                  <span class="text-xs font-semibold">${money(tutar)}</span>
                  <span class="text-[9px] font-bold rounded-full px-1.5 py-0.5 ${PAY[durum].chip}">${PAY[durum].label}</span>
                </span>
              </div>
              ${(inv.transactions ?? []).map(h => `
                <div class="flex items-center justify-between gap-2 text-[10px] text-inksoft mt-1">
                  <span class="truncate">${fmtDate(h.date)} · ${esc(h.method)} · makbuz ${esc(h.receiptNo)}</span>
                  <span class="shrink-0 text-teal font-semibold">${money(h.amount)}</span>
                </div>`).join("")}
              ${durum !== "odendi" ? `
                <div class="flex items-center justify-between gap-2 text-[10px] mt-1">
                  <span class="text-inksoft">Son ödeme ${fmtDate(inv.due)}</span>
                  <span class="shrink-0 font-semibold text-coral">${money(tutar - odenen)} kaldı</span>
                </div>` : ""}
              <div class="mt-1.5">${belgeDugmeleri(s, inv, s.branchId, { kalan: tutar - odenen })}</div>
            </div>`;
          }).join("")}
        </div>
      </div>
    </div>`, "min-w-0");
}
